import { defineStore } from 'pinia';
import { notify } from 'notiwind';
import _ from 'lodash';

export const useLoungesStore = defineStore('lounges', {
    state: () => ({
        activeLounge: null,
        activeAirport: null,
        activeFavoritesTab: 'Lounges',
        activeTripLeg: 1,
        airlines: [],
        airports: [],
        creditCards: [],
        classes: [
            { name: 'Economy', value: 'Economy' },
            { name: 'Premium Economy', value: 'PremiumEconomy' },
            { name: 'Business', value: 'Business' },
            { name: 'First', value: 'First' },
        ],
        error: null,
        loungeError: null,
        favoriteAirports: [],
        favoriteLounges: [],
        favoritesSearchTerm: '',
        filters: {
            airlines: [],
            amenities: [],
            cards: [],
            classes: [],
            flyerStatuses: [],
            memberships: [],
            openStatus: [],
            specialStatuses: [],
            terminals: [],
        },
        groupBy: null,
        statusesByAirline: [],
        loading: true,
        loadingMore: false,
        loungeMemberships: [],
        mapType: 'searchResults', // or  favoriteLounges, favoriteAirports, lounge
        openOptions: [{ label: 'Open Now', value: true }],
        operators: [],
        recentSearches: [],
        searchResults: null,
        searchResultsCount: 0,
        searchResultsUrl: '',
        searchState: 'loading', //or results, or empty, or error
        searchType: 'Find a lounge', // or 'Trip wizard'
        showLoungeFilterModal: false,
        showMap: false,
        sortBy: {},
        specialCircumstances: [],
        userCredentials: {
            memberships: [],
            elite_status: [],
            cards: [],
            special_circumstances: [],
        },
        userFlights: [],
    }),

    getters: {
        availableAirlines(state) {
            if (state.searchResults) {
                const airlines = Object.keys(state.searchResults).reduce(
                    (arr, terminal) => {
                        state.searchResults[terminal].forEach((lounge) => {
                            lounge.airline?.forEach((airline) => {
                                if (!arr.find((a) => a.name === airline.name))
                                    arr.push({
                                        name: airline.name,
                                        value: airline.code,
                                    });
                            });
                        });
                        return arr;
                    },
                    []
                );
                return airlines.sort((a, b) => a.name.localeCompare(b.name));
            }
            return [];
        },
        availableTerminals(state) {
            if (state.searchResults) {
                return Object.keys(state.searchResults)
                    .filter((terminal) => state.searchResults[terminal].length)
                    .map((terminal) => {
                        return {
                            value: terminal,
                        };
                    });
            }
            return [];
        },
        availableAmenities(state) {
            if (state.searchResults) {
                const amenities = Object.keys(state.searchResults).reduce(
                    (arr, terminal) => {
                        state.searchResults[terminal].forEach((lounge) => {
                            lounge.amenities?.forEach((amenity) => {
                                // Search results are cached for 48h server-side, and
                                // older payloads pass unknown amenities through as bare
                                // strings with no label to sort or filter on (SUPPORT-834).
                                if (
                                    typeof amenity?.label === 'string' &&
                                    !arr.find((a) => a.label === amenity.label)
                                ) {
                                    arr.push(amenity);
                                }
                            });
                        });
                        return arr;
                    },
                    []
                );
                return amenities.sort((a, b) => a.label.localeCompare(b.label));
            }
            return [];
        },
        mapCenter() {
            if (this.mapType === 'lounge' && this.activeLounge) {
                return {
                    lat: parseFloat(this.activeLounge.latitude),
                    lng: parseFloat(this.activeLounge.longitude),
                };
            }
            return null;
        },
        mapMarkers(state) {
            const arr = [];
            let flattenedResults = [];

            const pushMarker = (item) => {
                const name = item.name?.short || item.name;
                arr.push([
                    name,
                    parseFloat(item.latitude),
                    parseFloat(item.longitude),
                    item,
                ]);
            };

            switch (state.mapType) {
                case 'searchResults':
                    flattenedResults = Object.values(
                        this.filteredSearchResults
                    ).flat();
                    flattenedResults.forEach(pushMarker);
                    break;

                case 'favoriteLounges':
                    this.sortedFavoriteLounges.forEach((group) => {
                        flattenedResults.push(...Object.values(group));
                    });
                    flattenedResults.flat().forEach(pushMarker);
                    break;

                case 'lounge':
                    arr.push([
                        state.activeLounge.name.short,
                        parseFloat(state.activeLounge.latitude),
                        parseFloat(state.activeLounge.longitude),
                        state.activeLounge,
                    ]);
                    break;
                default:
                    break;
            }

            return arr;
        },
        filteredSearchResults() {
            return this.searchResults ? this.applyFilters() : {};
        },
        tripAirports(state) {
            if (state.userFlights.length) {
                const uniqueAirports = this.userFlights.reduce(
                    (acc, flight, i) => {
                        const to = {
                            ...flight.to,
                            class: flight.class,
                            airline: flight.airline,
                            ordinal: i * 2 + 2,
                        };
                        const from = {
                            ...flight.from,
                            class: flight.class,
                            airline: flight.airline,
                            ordinal: i * 2 + 1,
                        };

                        acc.airports.push(to);

                        const originAlreadyInAcc = acc.airports.some(
                            (airport) => airport.code === from.code
                        );
                        const flightAlreadyinAcc = acc.flights.some((f) => {
                            return (
                                f.class === flight.class &&
                                f.airline === flight.airline
                            );
                        });

                        if (originAlreadyInAcc && flightAlreadyinAcc) {
                            return acc;
                        } else {
                            acc.flights.push(flight);
                            acc.airports.push(from);
                        }
                        return acc;
                    },
                    { airports: [], flights: [] }
                );
                uniqueAirports.airports.sort((a, b) =>
                    a.ordinal > b.ordinal ? 1 : -1
                );
                return uniqueAirports;
            }
            return {};
        },
        sortedFavoriteAirports(state) {
            if (!state.favoriteAirports?.length) {
                return [];
            }
            // filter by search term:
            const filtered = state.favoriteAirports.filter(
                (a) =>
                    a.name
                        .toLowerCase()
                        .includes(state.favoritesSearchTerm.toLowerCase()) ||
                    a.code
                        .toLowerCase()
                        .includes(state.favoritesSearchTerm.toLowerCase()) ||
                    a.city
                        .toLowerCase()
                        .includes(state.favoritesSearchTerm.toLowerCase()) ||
                    a.country
                        .toLowerCase()
                        .includes(state.favoritesSearchTerm.toLowerCase())
            );
            // sort by sortBy:
            let sortDirection = 'desc';
            let sortValue = state.sortBy?.value || '-favorited_at';

            if (sortValue[0] != '-') {
                sortDirection = 'asc';
            } else {
                sortValue = sortValue.slice(1);
            }

            const sorted = filtered.sort((a, b) => {
                let aValue = a[sortValue];
                let bValue = b[sortValue];

                if (sortDirection === 'asc') {
                    return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
                } else {
                    return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
                }
            });
            // apply grouping if groupBy:
            if (state.groupBy) {
                return this.groupFavorites(sorted, state.groupBy.value);
            } else {
                return [
                    {
                        all: sorted,
                    },
                ];
            }
        },
        sortedFavoriteLounges(state) {
            if (!state.favoriteLounges.length) {
                return [];
            }
            // filter by search term:
            const filtered = state.favoriteLounges.filter((a) => {
                const searchTerm = state.favoritesSearchTerm.toLowerCase();
                const loungeName =
                    a.name?.long || a.name?.short || a.name || '';
                const airportName = a.airport?.name || '';

                return (
                    loungeName.toLowerCase().includes(searchTerm) ||
                    airportName.toLowerCase().includes(searchTerm)
                );
            });
            // sort by sortBy:
            let sortDirection = 'desc';
            let sortValue = state.sortBy?.value || '-favorited_at';

            if (sortValue[0] != '-') {
                sortDirection = 'asc';
            } else {
                sortValue = sortValue.slice(1);
            }

            const sorted = filtered.sort((a, b) => {
                let aValue, bValue;
                if (sortValue === 'name') {
                    aValue = a.name?.short || a.name?.long || a.name || '';
                    bValue = b.name?.short || b.name?.long || b.name || '';
                } else {
                    aValue = a[sortValue];
                    bValue = b[sortValue];
                }

                if (sortDirection === 'asc') {
                    return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
                } else {
                    return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
                }
            });

            // apply grouping if groupBy:
            if (state.groupBy) {
                return this.groupFavorites(sorted, state.groupBy.value);
            } else {
                return [
                    {
                        all: sorted,
                    },
                ];
            }
        },
    },
    actions: {
        hasFilterOptions() {
            return (
                this.airports.length &&
                this.creditCards.length &&
                this.airlines.length &&
                this.statusesByAirline.length &&
                this.loungeMemberships.length &&
                this.specialCircumstances.length
            );
        },
        async getFilterOptions() {
            if (!this.hasFilterOptions()) {
                this.setLoading(true);
                try {
                    const response = await ApiService.apiCall(
                        '/lounge-finder/filter-options',
                        'get'
                    );
                    this.setAirports(response.data.airports);
                    this.setCreditCards(response.data.cards);
                    this.setOperators(response.data.operators);
                    this.setAirlines(response.data.airlines);

                    this.setStatusesByAirline(
                        response.data.frequentFlyerPrograms
                    );
                    this.setLoungeMemberships(response.data.memberships);
                    this.setSpecialCircumstances(
                        response.data.specialCircumstances
                    );
                    this.setLoading(false);
                } catch (error) {
                    console.error(error);
                    this.setLoading(false);
                }
            }
        },
        // Cache management
        getCachedResults(airportCode) {
            const cachedResults = sessionStorage.getItem(
                `lounges-${airportCode}`
            );
            if (!cachedResults) return null;

            const parsedResults = JSON.parse(cachedResults);
            const cacheTime = parsedResults.timestamp;

            // Cache for 1 hour
            if (Date.now() - cacheTime > 60 * 60 * 1000) return null;

            return parsedResults;
        },

        setCachedResults(airportCode, lounges, airport) {
            const resultsToCache = {
                lounges,
                airport,
                timestamp: Date.now(),
            };
            sessionStorage.setItem(
                `lounges-${airportCode}`,
                JSON.stringify(resultsToCache)
            );
        },
        resetSearchState() {
            this.setLoading(true);
            this.setSearchResults(null);
            this.setSearchState('loading');
            this.setError(null);
            this.setSearchResultsCount(0);
        },
        async updateSearchResults(lounges, airport) {
            if (!this.favoriteLounges.length) {
                await this.getUserFavorites('lounge');
            }
            if (!this.favoriteAirports.length) {
                await this.getUserFavorites('airport');
            }

            // A failed search returns an error payload with no lounges map.
            const updatedLounges = Object.entries(lounges ?? {}).reduce(
                (acc, [key, loungeList]) => {
                    acc[key] = loungeList.map((lounge) => ({
                        ...lounge,
                        favorite: this.favoriteLounges.some(
                            (fav) => fav.id == lounge.id
                        ),
                    }));
                    return acc;
                },
                {}
            );

            this.setSearchResults(updatedLounges);

            const count = Object.values(updatedLounges)?.flat().length;
            this.setSearchResultsCount(count);

            // Set search state based on initial results
            if (count === 0) {
                this.setSearchState('empty');
            } else {
                this.setSearchState('results');
            }

            // Keep the selected airport when a response doesn't carry one.
            if (airport) {
                const updatedAirport = {
                    ...airport,
                    favorite: this.favoriteAirports.some(
                        (fav) => fav.id == airport.id
                    ),
                };
                this.setActiveAirport(updatedAirport);
            }
        },

        // Search lounges by airport:
        async searchLounges() {
            this.resetSearchState();

            try {
                /*const cachedResults = this.getCachedResults(
                    this.activeAirport.code
                );
                if (cachedResults) {
                    this.updateSearchResults(
                        cachedResults.lounges,
                        cachedResults.airport
                    );
                    this.setLoading(false);
                    return;
                }*/

                const response = await ApiService.apiCall(
                    `/lounge-finder/search/${this.activeAirport.code}`,
                    'get'
                );

                // A failed lookup comes back as an error body with no lounges.
                // Treat it as a failed search so the selected airport stays
                // put for a retry (SUPPORT-834).
                if (!response.data?.lounges) {
                    throw new Error(
                        response.data?.error || 'Lounge search failed'
                    );
                }

                this.setCachedResults(
                    this.activeAirport.code,
                    response.data.lounges,
                    response.data.airport
                );

                this.updateSearchResults(
                    response.data.lounges,
                    response.data.airport
                );
            } catch (error) {
                notify(
                    {
                        group: 'error',
                        title: 'Search Error',
                        text: 'Failed to search lounges. Please try again.',
                    },
                    4000
                );
                this.setError(error);
                this.setSearchResults(null);
            } finally {
                this.setLoading(false);
            }
        },
        async getLounge(id) {
            this.setLoading(true);
            this.loungeError = null;
            try {
                const response = await ApiService.apiCall(
                    `/lounge-finder/${id}`,
                    'get'
                );

                if (response.data?.error) {
                    this.loungeError =
                        response.data.message ||
                        'This lounge could not be found.';
                    this.setActiveLounge(null);
                    return;
                }

                this.setActiveLounge(response.data);
                this.setActiveAirport(response.data.airport);
            } catch (error) {
                this.loungeError =
                    'Unable to load lounge details. Please try again later.';
                this.setActiveLounge(null);
            } finally {
                this.setLoading(false);
            }
        },
        async searchWizard() {
            this.resetSearchState();
            const flights = this.userFlights.map((f) => ({
                airline: f.airline.code,
                origin: f.from.code,
                destination: f.to.code,
                cos: f.class.value,
            }));

            let data = {
                elite_status: this.userCredentials?.elite_status.length
                    ? this.transformEliteStatusForSearch(
                          this.userCredentials.elite_status
                      )
                    : [],
                memberships: this.userCredentials?.memberships.length
                    ? this.transformMembershipsForSearch(
                          this.userCredentials.memberships
                      )
                    : [],
                credit_cards: this.userCredentials?.cards.length
                    ? this.transformCardsForSearch(this.userCredentials.cards)
                    : [],
                special_circumstances: this.userCredentials
                    ?.special_circumstances.length
                    ? this.transformSpecialCircumstancesForSearch(
                          this.userCredentials.special_circumstances
                      )
                    : [],
                flights: flights,
                airport_number: this.activeTripLeg,
            };

            try {
                const response = await ApiService.apiCall(
                    '/lounge-finder/trip-wizard',
                    'post',
                    data
                );
                const filteredLounges = this.filterInaccessibleLounges(
                    response.data.lounges
                );

                this.updateSearchResults(
                    filteredLounges,
                    response.data.airport
                );
            } catch (error) {
                console.error(error);
            } finally {
                this.setLoading(false);
            }
        },
        applyFilters() {
            if (this.searchResults) {
                return Object.keys(this.searchResults).reduce(
                    (acc, terminal) => {
                        acc[terminal] = this.searchResults[terminal].filter(
                            (lounge) => {
                                let airlines = true;
                                let amenities = true;
                                let cards = true;
                                let classes = true;
                                let memberships = true;
                                let terminals = true;
                                let specialStatuses = true;
                                let openStatus = true;
                                let frequentFlyerPrograms = true;

                                if (this.filters.airlines.length > 0) {
                                    airlines = lounge.airline.some((airline) =>
                                        this.filters.airlines.some(
                                            (a) => a.value === airline.code
                                        )
                                    );
                                }

                                if (this.filters.amenities.length > 0) {
                                    amenities = this.filters.amenities.every(
                                        (amenity) =>
                                            lounge.amenities.some(
                                                (a) => a.value === amenity.value
                                            )
                                    );
                                }

                                if (this.filters.terminals.length > 0) {
                                    terminals = this.filters.terminals.some(
                                        (t) => {
                                            return t.value == lounge.terminal;
                                        }
                                    );
                                }

                                if (this.filters.openStatus.length > 0) {
                                    openStatus = lounge.open_now === true;
                                }

                                return (
                                    airlines &&
                                    terminals &&
                                    classes &&
                                    memberships &&
                                    openStatus &&
                                    specialStatuses &&
                                    frequentFlyerPrograms &&
                                    cards &&
                                    amenities
                                );
                            }
                        );
                        return acc;
                    },
                    {}
                );
            }
            return null;
        },
        filterInaccessibleLounges(resultsObj) {
            if (!Object.keys(resultsObj).length) {
                return resultsObj;
            }
            return Object.keys(resultsObj).reduce((acc, terminal) => {
                acc[terminal] = resultsObj[terminal].filter((lounge) => {
                    return (
                        lounge.access_modes.length > 1 ||
                        (lounge.access_modes.length == 1 &&
                            lounge.access_modes[0].access_type > 0)
                    );
                });
                return acc;
            }, {});
        },
        async toggleFavorite(item, type) {
            if (
                !item ||
                typeof item === 'undefined' ||
                !item?.id ||
                typeof item.id === 'undefined' ||
                item.id === 'undefined'
            ) {
                console.error('toggleFavorite called with invalid item:', item);
                return false;
            }

            try {
                const response = await ApiService.apiCall(
                    `/lounge-finder/fav/${type}/${item.id}`,
                    'get'
                );
                // optimistic render:
                if (type === 'airport') {
                    const itemIndex = this.favoriteAirports.findIndex(
                        (fav) => fav.id === item.id
                    );
                    if (itemIndex >= 0) {
                        this.favoriteAirports.splice(itemIndex, 1);
                    } else {
                        this.favoriteAirports.push(item);
                    }
                }
                if (type === 'lounge') {
                    const itemIndex = this.favoriteLounges.findIndex(
                        (fav) => fav.id === item.id
                    );
                    if (itemIndex >= 0) {
                        this.favoriteLounges.splice(itemIndex, 1);
                    } else {
                        this.favoriteLounges.push(item);
                    }
                }

                return response;
            } catch (err) {
                console.error(err);
                await this.getUserFavorites(type);
                notify({
                    group: 'error',
                    title: 'Error',
                    text: 'There was an error saving your changes. Please try again.',
                });
                return null;
            }
        },
        async getUserFavorites(type) {
            this.setLoading(true);
            try {
                if (type === 'lounge') {
                    // Step 1: Get cached lounges instantly
                    const cachedRes = await ApiService.apiCall(
                        '/lounge-finder/lounge/favorites/cached',
                        'get'
                    );

                    const cachedLounges = cachedRes.data.lounges || [];
                    cachedLounges.forEach((item) => {
                        item.favorite = true;
                    });

                    this.setFavoriteLounges(cachedLounges);
                    this.setLoading(false);

                    // Step 2: Fetch uncached lounges in the background
                    const uncachedIds = cachedRes.data.uncachedIds || [];
                    if (uncachedIds.length) {
                        this.loadingMore = true;
                        try {
                            const uncachedRes = await ApiService.apiCall(
                                '/lounge-finder/lounge/favorites/uncached',
                                'post',
                                { ids: uncachedIds }
                            );

                            const uncachedLounges = uncachedRes.data || [];
                            uncachedLounges.forEach((item) => {
                                item.favorite = true;
                            });

                            this.setFavoriteLounges([
                                ...this.favoriteLounges,
                                ...uncachedLounges,
                            ]);
                        } catch (error) {
                            console.error(
                                'Error loading uncached favorites:',
                                error
                            );
                        } finally {
                            this.loadingMore = false;
                        }
                    }
                } else {
                    const res = await ApiService.apiCall(
                        `/lounge-finder/${type}/favorites`,
                        'get'
                    );
                    const data = res.data;

                    data.forEach((item) => {
                        item.favorite = true;
                    });

                    if (type === 'airport') {
                        this.setFavoriteAirports(data);
                    }

                    this.setLoading(false);
                }
            } catch (error) {
                console.error(`Error loading ${type} favorites:`, error);
                this.setLoading(false);
                this.loadingMore = false;
            }
        },
        groupFavorites(arr, groupBy) {
            let grouped = {};
            if (this.activeFavoritesTab === 'Lounges') {
                grouped = arr.reduce((acc, curr) => {
                    if (!acc[curr.airport[groupBy]]) {
                        acc[curr.airport[groupBy]] = [];
                    }
                    acc[curr.airport[groupBy]].push(curr);
                    return acc;
                }, {});
            } else if (this.activeFavoritesTab === 'Airports') {
                grouped = arr.reduce((acc, curr) => {
                    if (!acc[curr[groupBy]]) {
                        acc[curr[groupBy]] = [];
                    }
                    acc[curr[groupBy]].push(curr);
                    return acc;
                }, {});
            }
            const sortedKeys = Object.keys(grouped).sort((a, b) =>
                a.localeCompare(b)
            );
            const groupedArray = sortedKeys.map((key) => ({
                [key]: grouped[key],
            }));
            return groupedArray;
        },
        resetFilters() {
            this.filters = {
                airlines: [],
                amenities: [],
                classes: [],
                openStatus: [],
                terminals: [],
            };

            // Update the search results count when filters are reset
            this.updateFilteredResultsCount();
        },
        resetUserCredentials() {
            this.userCredentials = {
                cards: [],
                elite_status: [],
                memberships: [],
                special_circumstances: [],
            };
        },
        setFavoriteAirports(arr) {
            this.favoriteAirports = arr;
        },
        setFavoriteLounges(arr) {
            this.favoriteLounges = arr;
        },
        setActiveAirport(val) {
            this.activeAirport = val;
        },
        setActiveFavoritesTab(s) {
            this.activeFavoritesTab = s;
        },
        setActiveLounge(val) {
            this.activeLounge = val;
        },
        setAirports(data) {
            this.airports = data;
        },
        setFavoritesSearchTerm(val) {
            this.favoritesSearchTerm = val;
        },
        setMapType(val) {
            this.mapType = val;
        },
        setSearchResults(data) {
            this.searchResults = data;
        },
        setShowLoungeFilterModal(val) {
            this.showLoungeFilterModal = val;
        },
        setShowMap(val) {
            this.showMap = val;
        },
        setCreditCards(data) {
            this.creditCards = data;
        },
        setOperators(data) {
            this.operators = data;
        },
        setAirlines(data) {
            this.airlines = data;
        },
        setLoading(val) {
            this.loading = val;
        },
        setSpecialCircumstances(val) {
            this.specialCircumstances = val;
        },
        setStatusesByAirline(val) {
            this.statusesByAirline = val;
        },
        setLoungeMemberships(val) {
            this.loungeMemberships = val;
        },
        setSearchType(val) {
            this.searchType = val;
        },
        setFilters(obj) {
            this.filters = obj;

            // Update the search results count when filters are set
            this.updateFilteredResultsCount();
        },
        setSingleFilter(key, arr) {
            const copy = { ...this.filters };
            copy[key] = arr;
            this.filters = copy;

            // Update the search results count when filters change
            this.updateFilteredResultsCount();
        },
        setUserFlights(array) {
            this.userFlights = array;
        },
        setActiveTripLeg(num) {
            this.activeTripLeg = num;
        },
        setUserCredentials(obj) {
            this.userCredentials = obj;
        },
        setSearchResultsCount(num) {
            this.searchResultsCount = num;
        },
        setError(val) {
            this.error = val;
        },
        setGroupBy(val) {
            this.groupBy = val;
        },
        setSortBy(val) {
            this.sortBy = val;
        },
        setSearchState(val) {
            this.searchState = val;
        },
        isOpen(hoursArray, timeZone) {
            return true;
        },
        async findAirportByCode(code) {
            if (!code) return null;
            try {
                const response = await ApiService.apiCall(
                    `/airports/search?search=${encodeURIComponent(code)}`,
                    'get'
                );
                return (
                    response.data?.find(
                        (a) => a.code.toUpperCase() === code.toUpperCase()
                    ) || null
                );
            } catch (error) {
                console.error(`Error fetching airport ${code}:`, error);
                return null;
            }
        },
        transformCardsForSearch(arr) {
            return arr
                .filter((c) => c.lr_card_code !== null)
                .map((c) => c.lr_card_code);
        },
        transformMembershipsForSearch(arr) {
            return arr.filter((m) => m.code !== null).map((m) => m.code);
        },
        transformEliteStatusForSearch(arr) {
            return arr
                .filter((e) => e.status_code !== null)
                .map((e) => e.status_code);
        },
        transformSpecialCircumstancesForSearch(arr) {
            return arr.filter((s) => s.code !== null).map((s) => s.code);
        },
        buildResultsUrl(trips) {
            const params = new URLSearchParams();
            const baseUrl = '/lounges/wizard';

            trips.forEach((trip, index) => {
                params.append('o[]', trip.from.code);
                params.append('d[]', trip.to.code);
                params.append('a[]', trip.airline.code);
                params.append('c[]', trip.class.value);
            });
            params.append('seg', this.activeTripLeg);

            const fullUrl = `${baseUrl}?${params.toString()}`;

            return fullUrl;
        },
        async parseTripsFromUrl() {
            let items = this.parseQueryParamsWithBrackets(
                window.location.search
            );
            items = this.convertToObjectArray(items);

            const flights = [];
            let activeTripLeg = null;

            // Collect unique airport codes to fetch
            const airportCodes = new Set();
            items.forEach((item) => {
                if (item.o) airportCodes.add(item.o.toUpperCase());
                if (item.d) airportCodes.add(item.d.toUpperCase());
            });

            // Fetch airports from API
            const airportMap = new Map();
            for (const code of airportCodes) {
                try {
                    const response = await ApiService.apiCall(
                        `/airports/search?search=${encodeURIComponent(code)}`,
                        'get'
                    );
                    const airport = response.data?.find(
                        (a) => a.code.toUpperCase() === code
                    );
                    if (airport) {
                        airportMap.set(code, airport);
                    }
                } catch (error) {
                    console.error(`Error fetching airport ${code}:`, error);
                }
            }

            items.forEach((item) => {
                const flight = {
                    from: airportMap.get(item.o?.toUpperCase()) || null,
                    to: airportMap.get(item.d?.toUpperCase()) || null,
                    airline: this.airlines.find((a) => a.code === item.a),
                    class: this.classes.find((c) => c.value === item.c),
                };
                flights.push(flight);
            });

            activeTripLeg = items[0]?.seg || null;

            return { flights, activeTripLeg };
        },
        parseQueryParamsWithBrackets(queryString) {
            const params = new URLSearchParams(queryString);
            const result = {};
            for (const [key, value] of params.entries()) {
                const cleanedKey = key.replace(/\[.*?\]$/, '');
                if (result[cleanedKey]) {
                    if (!Array.isArray(result[cleanedKey])) {
                        result[cleanedKey] = [result[cleanedKey]];
                    }
                    result[cleanedKey].push(value);
                } else {
                    result[cleanedKey] = value;
                }
            }
            return result;
        },
        convertToObjectArray(data) {
            const keys = Object.keys(data);
            const maxLength = Math.max(...keys.map((key) => data[key].length));
            return Array.from({ length: maxLength }, (_, index) => {
                const obj = {};
                keys.forEach((key) => {
                    const values = data[key];
                    obj[key] = values[Math.min(index, values.length - 1)];
                });
                return obj;
            });
        },
        async getRecentSearches() {
            try {
                const response = await ApiService.apiCall(
                    '/tools/loungeSearchLog',
                    'post'
                );
                if (response.data) {
                    this.recentSearches = response.data;
                } else {
                    throw new Error(
                        'Unable to retrieve recent lounge searches.'
                    );
                }
            } catch (error) {
                console.error('Error fetching recent lounge searches:', error);
                notify({
                    type: 'error',
                    text: 'Unable to retrieve recent lounge searches.',
                });
            }
        },
        updateFilteredResultsCount() {
            if (this.searchResults) {
                const filteredResults = this.filteredSearchResults;
                const filteredCount =
                    Object.values(filteredResults).flat().length;
                this.setSearchResultsCount(filteredCount);

                // Update search state based on filtered results
                if (filteredCount === 0) {
                    this.setSearchState('empty');
                } else {
                    this.setSearchState('results');
                }
            }
        },
    },
});
